/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useOpenVPN.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useContextStore } from '../../../store/context';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import * as api from '../api/openvpnApi';
import type {
    OpenVPNConfig,
    OpenVPNConfigFormState,
    OpenVPNClient,
    CreateClientRequest,
    DEFAULT_OPENVPN_CONFIG
} from '../types';

export function useOpenVPN() {
    const { user } = useAuthStore();
    const { selectedTenant } = useContextStore();
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();
    const tenantSlug = selectedTenant?.slug || 'main';

    // Config state
    const [config, setConfig] = useState<OpenVPNConfig | null>(null);
    const [configFormData, setConfigFormData] = useState<OpenVPNConfigFormState>({
        enabled: false,
        serverAddress: '',
        serverPort: 1194,
        protocol: 'udp',
        deviceType: 'tun',
        vpnSubnet: '10.8.0.0',
        vpnNetmask: '255.255.255.0',
        dnsServer1: '8.8.8.8',
        dnsServer2: '8.8.4.4',
        cipher: 'AES-256-GCM',
        auth: 'SHA256',
        tlsVersionMin: '1.2',
        keepaliveInterval: 10,
        keepaliveTimeout: 120,
        maxClients: 100,
        compress: 'no'
    });
    const [originalConfigData, setOriginalConfigData] = useState<OpenVPNConfigFormState | null>(null);

    // Clients state
    const [clients, setClients] = useState<OpenVPNClient[]>([]);

    // Loading states
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [loadingClients, setLoadingClients] = useState(true);
    const [savingConfig, setSavingConfig] = useState(false);
    const [creatingClient, setCreatingClient] = useState(false);

    // Load config
    const loadConfig = useCallback(async () => {
        setLoadingConfig(true);
        try {
            const response = await api.getOpenVPNConfig(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConfig(response.data);
                const formState: OpenVPNConfigFormState = {
                    enabled: response.data.enabled,
                    serverAddress: response.data.serverAddress,
                    serverPort: response.data.serverPort,
                    protocol: response.data.protocol,
                    deviceType: response.data.deviceType,
                    vpnSubnet: response.data.vpnSubnet,
                    vpnNetmask: response.data.vpnNetmask,
                    dnsServer1: response.data.dnsServer1,
                    dnsServer2: response.data.dnsServer2,
                    cipher: response.data.cipher,
                    auth: response.data.auth,
                    tlsVersionMin: response.data.tlsVersionMin,
                    keepaliveInterval: response.data.keepaliveInterval,
                    keepaliveTimeout: response.data.keepaliveTimeout,
                    maxClients: response.data.maxClients,
                    compress: response.data.compress
                };
                setConfigFormData(formState);
                setOriginalConfigData(formState);
            }
        } catch (error) {
            console.error('Failed to load OpenVPN config:', error);
            showError(t('openvpn.messages.loadConfigError', 'Failed to load OpenVPN configuration'));
        } finally {
            setLoadingConfig(false);
        }
    }, [tenantSlug, user?.id, showError, t]);

    // Load clients
    const loadClients = useCallback(async () => {
        setLoadingClients(true);
        try {
            const response = await api.getOpenVPNClients(tenantSlug, user?.id);
            if (response.success) {
                setClients(response.data || []);
            }
        } catch (error) {
            console.error('Failed to load OpenVPN clients:', error);
            showError(t('openvpn.messages.loadClientsError', 'Failed to load OpenVPN clients'));
        } finally {
            setLoadingClients(false);
        }
    }, [tenantSlug, user?.id, showError, t]);

    // Save config
    const saveConfig = useCallback(async () => {
        // Validate required fields
        if (!configFormData.serverAddress || configFormData.serverAddress.trim() === '') {
            showError(t('openvpn.messages.publicHostRequired', 'Public Host is required'));
            return false;
        }
        if (!configFormData.serverPort || configFormData.serverPort < 1 || configFormData.serverPort > 65535) {
            showError(t('openvpn.messages.serverPortRequired', 'Server Port is required and must be between 1 and 65535'));
            return false;
        }

        setSavingConfig(true);
        try {
            const response = await api.updateOpenVPNConfig(configFormData, tenantSlug, user?.id);
            if (response.success) {
                setConfig(response.data);
                setOriginalConfigData(configFormData);
                showSuccess(t('openvpn.messages.saveConfigSuccess', 'OpenVPN configuration saved successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to save OpenVPN config:', error);
            showError(error.message || t('openvpn.messages.saveConfigError', 'Failed to save OpenVPN configuration'));
            return false;
        } finally {
            setSavingConfig(false);
        }
    }, [configFormData, tenantSlug, user?.id, showSuccess, showError, t]);

    // Create client
    const createClient = useCallback(async (data: CreateClientRequest) => {
        setCreatingClient(true);
        try {
            const response = await api.createOpenVPNClient(data, tenantSlug, user?.id);
            if (response.success) {
                setClients(prev => [...prev, response.data]);
                showSuccess(t('openvpn.messages.createClientSuccess', 'Client certificate created successfully'));
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Failed to create OpenVPN client:', error);
            showError(error.message || t('openvpn.messages.createClientError', 'Failed to create client certificate'));
            return null;
        } finally {
            setCreatingClient(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Delete client
    const deleteClient = useCallback(async (clientId: number) => {
        try {
            const response = await api.deleteOpenVPNClient(clientId, tenantSlug, user?.id);
            if (response.success) {
                setClients(prev => prev.filter(c => c.id !== clientId));
                showSuccess(t('openvpn.messages.deleteClientSuccess', 'Client deleted successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to delete OpenVPN client:', error);
            showError(error.message || t('openvpn.messages.deleteClientError', 'Failed to delete client'));
            return false;
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Revoke client
    const revokeClient = useCallback(async (clientId: number) => {
        try {
            const response = await api.revokeOpenVPNClient(clientId, tenantSlug, user?.id);
            if (response.success) {
                setClients(prev => prev.map(c =>
                    c.id === clientId ? { ...c, revokedAt: new Date().toISOString() } : c
                ));
                showSuccess(t('openvpn.messages.revokeClientSuccess', 'Client certificate revoked successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to revoke OpenVPN client:', error);
            showError(error.message || t('openvpn.messages.revokeClientError', 'Failed to revoke client certificate'));
            return false;
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Download client config
    const downloadConfig = useCallback(async (clientId: number) => {
        try {
            // API function handles the download internally
            await api.downloadClientConfig(clientId, tenantSlug, user?.id);
            return true;
        } catch (error) {
            console.error('Failed to download client config:', error);
            showError(error.message || t('openvpn.messages.downloadError', 'Failed to download configuration'));
            return false;
        }
    }, [tenantSlug, user?.id, showError, t]);

    // Refresh client status
    const refreshStatus = useCallback(async () => {
        try {
            const response = await api.refreshClientStatus(tenantSlug, user?.id);
            if (response.success) {
                setClients(response.data || []);
            }
        } catch (error) {
            console.error('Failed to refresh client status:', error);
        }
    }, [tenantSlug, user?.id]);

    // Update config field
    const updateConfigField = useCallback(<K extends keyof OpenVPNConfigFormState>(
        field: K,
        value: OpenVPNConfigFormState[K]
    ) => {
        setConfigFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Cancel config changes
    const cancelConfigChanges = useCallback(() => {
        if (originalConfigData) {
            setConfigFormData(originalConfigData);
        }
    }, [originalConfigData]);

    // Check if config has changes
    const hasConfigChanges = originalConfigData
        ? JSON.stringify(configFormData) !== JSON.stringify(originalConfigData)
        : false;

    // Load data on mount
    useEffect(() => {
        loadConfig();
        loadClients();
    }, [loadConfig, loadClients]);

    return {
        // Config
        config,
        configFormData,
        loadingConfig,
        savingConfig,
        hasConfigChanges,
        updateConfigField,
        saveConfig,
        cancelConfigChanges,

        // Clients
        clients,
        loadingClients,
        creatingClient,
        createClient,
        deleteClient,
        revokeClient,
        downloadConfig,
        refreshStatus,
        reloadClients: loadClients
    };
}
